import BibliotecaLibrosPage from "./components/integrador/resumen_libros/BibliotecaResumida";
import GestorUsuarios from "./components/gestor_usuarios/GestorUsuarios";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import DashboardPrincipal from "./pages/dashboard/DashboardPrincipal";
import Traductor from "./components/integrador/traductor/traductor";
import Cuestionador from "./components/integrador/cuestionario";
import Buscador from "./components/buscador/SesionSearchPage";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { createTheme, CssBaseline, ThemeProvider, useMediaQuery } from "@mui/material";
import ResumenLibros from "./components/integrador/libro";
import SACWizard from "./components/admin/sac/SACWizard";
import PublicNavBar from "./components/PublicNavBar";
import AdmSAEForm from "./components/root/saeForm";
import bg_general from "./assets/pattern_bg.jpg";
import NavBar from "./components/NavBar";
import Login from "./pages/login/Login";
import Home from "./pages/home/Home";
import { HERRAMIENTAS_DOCENTE_HABILITADAS } from "./pages/home/config/homeData";

const appTheme = createTheme({
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: "#9f9f9f",
          color: "#ffffff",
          fontWeight: 700,
        },
      },
    },
  },
});

const PrivateRoute = ({ children, role }) => {
  const { isAuthenticated, user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) return null;

  if (!isAuthenticated) {
    return (
      <Navigate
        to="/"
        state={{ from: location }}
        replace
      />
    );
  }

  if (role && user?.rol?.nombre !== role) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const LayoutNavBar = () => {
  const { isAuthenticated } = useAuth();

  const isMobile = useMediaQuery("(max-width:800px)");

  if (isAuthenticated) {
    return <NavBar />;
  }

  return isMobile ? <PublicNavBar /> : null;
};

const AppContent = () => {
  const { isAuthenticated, user, initializing } = useAuth();
  const location = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  if (initializing) return null;

  const RUTAS_CON_FONDO = [
    "/admin/sac",
    "/director/sac",
    "/profesor/sac",
    "/admin/sae",
    "/admin/buscador",
    "/director/buscador",
    "/profesor/buscador",
    ...(HERRAMIENTAS_DOCENTE_HABILITADAS
      ? [
          "/cuestionario",
          "/traductor",
          "/resumenlibro",
          "/resumen-biblioteca",
        ]
      : []),
    "/admin/dashboard",
    "/director/dashboard",
    "/profesor/dashboard",
    "/admin/gestor-usuarios"
  ];

  const showBackground = RUTAS_CON_FONDO.includes(location.pathname);
  const bgStyle = `url(${bg_general})`;

  const ROLE_HOME = {
    ADMIN: "/admin/home",
    DIRECTOR: "/director/home",
    PROFESOR: "/profesor/home",
  };

  const getHome = (user) =>
    ROLE_HOME[user?.rol?.nombre];

  return (
    <div style={{ position: "relative", minHeight: "100vh", backgroundColor: "#ffffff" }}>
      {showBackground && (
        <div
          style={{
            backgroundImage: bgStyle,
            backgroundRepeat: "repeat",
            backgroundSize: "50vw",
            opacity: 0.05,
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
          }}
        />
      )}

      <div style={{ position: "relative", zIndex: 1 }}>
        <CssBaseline />
        <LayoutNavBar />

        <Routes key={location.pathname}>
          <Route
            path="/"
            element={
              isAuthenticated
                ? <Navigate to={getHome(user)} replace />
                : <Login />
            }
          />

          {HERRAMIENTAS_DOCENTE_HABILITADAS && (
            <>
              <Route path="/traductor" element={<PrivateRoute><Traductor /></PrivateRoute>} />
              <Route path="/cuestionario" element={<PrivateRoute><Cuestionador /></PrivateRoute>} />
              <Route path="/resumenlibro" element={<PrivateRoute><ResumenLibros /></PrivateRoute>} />
              <Route path="/resumen-biblioteca" element={<PrivateRoute><BibliotecaLibrosPage /></PrivateRoute>} />
            </>
          )}

          <Route path="/admin/home" element={<PrivateRoute role="ADMIN"><Home role="admin" /></PrivateRoute>} />
          <Route path="/director/home" element={<PrivateRoute role="DIRECTOR"><Home role="director" /></PrivateRoute>} />
          <Route path="/profesor/home" element={<PrivateRoute role="PROFESOR"><Home role="profesor" /></PrivateRoute>} />

          <Route path="/admin/sac" element={<PrivateRoute role="ADMIN"><SACWizard userRole="ADMIN" /></PrivateRoute>} />
          <Route path="/director/sac" element={<PrivateRoute role="DIRECTOR"><SACWizard userRole="DIRECTOR" /></PrivateRoute>} />
          <Route path="/profesor/sac" element={<PrivateRoute role="PROFESOR"><SACWizard userRole="PROFESOR" /></PrivateRoute>} />

          <Route path="/admin/sae" element={<PrivateRoute role="ADMIN"><AdmSAEForm /></PrivateRoute>} />
          <Route path="/admin/gestor-usuarios" element={<PrivateRoute role="ADMIN"><GestorUsuarios /></PrivateRoute>} />

          <Route path="/admin/buscador" element={<PrivateRoute role="ADMIN"><Buscador /></PrivateRoute>} />
          <Route path="/director/buscador" element={<PrivateRoute role="DIRECTOR"><Buscador /></PrivateRoute>} />
          <Route path="/profesor/buscador" element={<PrivateRoute role="PROFESOR"><Buscador /></PrivateRoute>} />

          <Route path="/admin/dashboard" element={<PrivateRoute role="ADMIN"><DashboardPrincipal /></PrivateRoute>} />
          <Route path="/director/dashboard" element={<PrivateRoute role="DIRECTOR"><DashboardPrincipal /></PrivateRoute>} />
          <Route path="/profesor/dashboard" element={<PrivateRoute role="PROFESOR"><DashboardPrincipal /></PrivateRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
};

const App = () => (
  <ThemeProvider theme={appTheme}>
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  </ThemeProvider>
);

export default App;
